import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Suspense, useMemo } from "react";
import { PageHero } from "@/components/site/PageHero";
import { activeItems, galleryItemsQueryOptions } from "@/lib/gallery.queries";
import {
  EmptyState,
  FeaturedVideo,
  SectionHeading,
  VideoGrid,
} from "@/components/site/GallerySections";

type BeautyShowSearch = {
  /** Legacy miinews.com deep link: /beauty-show?video=<permalink>. */
  video?: string;
  /** Site-wide design-direction override (?v=v1..v4) — kept so shared links still work. */
  v?: string;
};

export const Route = createFileRoute("/beauty-show")({
  validateSearch: (search: Record<string, unknown>): BeautyShowSearch => ({
    video: typeof search.video === "string" && search.video ? search.video : undefined,
    v: typeof search.v === "string" && search.v ? search.v : undefined,
  }),
  beforeLoad: ({ search }) => {
    if (search.video) {
      throw redirect({
        to: "/beauty-show/$permalink",
        params: { permalink: search.video },
        search: { ...search, video: undefined },
        replace: true,
      });
    }
  },
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(galleryItemsQueryOptions("beauty-episode"));
  },
  head: () => ({
    meta: [
      { title: "The Beauty Show — Medical Insight" },
      {
        name: "description",
        content:
          "The Beauty Show — episodes and interviews from Medical Insight covering the people, products, and business of medical aesthetics.",
      },
      { property: "og:title", content: "The Beauty Show — Medical Insight" },
    ],
  }),
  component: BeautyShowPage,
});

function BeautyShowPage() {
  return (
    <Suspense
      fallback={
        <div className="mx-auto max-w-7xl px-4 py-16 text-sm text-muted-foreground">
          Loading episodes…
        </div>
      }
    >
      <BeautyShowContent />
    </Suspense>
  );
}

function BeautyShowContent() {
  const { data: raw } = useSuspenseQuery(galleryItemsQueryOptions("beauty-episode"));
  const episodes = useMemo(() => activeItems(raw), [raw]);
  const [featured, ...rest] = episodes;

  return (
    <div className="bg-background">
      <PageHero
        page="beautyshow"
        kicker="Medical Insight"
        title="The Beauty Show"
      />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:py-14">
        {episodes.length === 0 ? (
          <EmptyState
            title="No episodes yet"
            message="New episodes of The Beauty Show will appear here as they are published."
          />
        ) : (
          <>
            {featured && (
              <section className="mb-12">
                <FeaturedVideo item={featured} basePath="/beauty-show" label="Latest episode" />
              </section>
            )}
            {rest.length > 0 && (
              <section>
                <SectionHeading title="More episodes" />
                <VideoGrid items={rest} basePath="/beauty-show" />
              </section>
            )}
          </>
        )}
      </div>

      {/* /beauty-show/$permalink renders the episode lightbox over this page. */}
      <Outlet />
    </div>
  );
}
